import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import LegalProtocolPanel from "@/components/LegalProtocolPanel";
import { ArrowLeft, FilePdf, Printer, Shield } from "@phosphor-icons/react";

/**
 * Legal Protocol deep-dive page
 * -----------------------------
 * A standalone view of one WO's Legal Protocol. Purpose:
 *   – Print-friendly layout (no sidebar, big text) for the compliance
 *     folder the office keeps per unit
 *   – Room for tools that don't fit on the WO detail (chain-of-custody
 *     timeline, jump to Legal Radar, evidence-freeze button — TODO)
 *
 * Route: /admin/jobs/:id/legal-protocol
 *
 * The main WO detail page has a "Deep-dive" button in the top-right of
 * the inline LegalProtocolPanel that links here.
 */
export default function AdminJobLegalProtocol() {
  const { id } = useParams();
  const [job, setJob] = useState(null);

  useEffect(() => {
    api.get(`/jobs/${id}`).then(r => setJob(r.data)).catch(() => setJob({}));
  }, [id]);

  if (!job) return <AdminShell><div className="p-8">Loading…</div></AdminShell>;

  const lp = job.legal_protocol || {};

  return (
    <AdminShell>
      <div className="max-w-3xl mx-auto p-4 sm:p-6 print:p-0">
        {/* Screen-only breadcrumb — hides on print for a clean compliance PDF */}
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-3 print:hidden">
          <Link to={`/admin/jobs/${id}`} className="hover:text-slate-900 inline-flex items-center gap-1">
            <ArrowLeft size={12}/> Back to WO
          </Link>
          <span>·</span>
          <span>Legal Protocol Deep-Dive</span>
        </div>

        <header className="mb-4 pb-4 border-b-2 border-slate-200">
          <div className="flex items-center gap-2 mb-1">
            <Shield weight="fill" size={22} className="text-red-700"/>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              Legal Protocol · Compliance Packet
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight">
            WO #{job.work_order_number || (job.id || "").slice(-6)}
            {lp.category_label && <span className="text-slate-400 font-medium ml-2 text-lg">· {lp.category_label}</span>}
          </h1>
          <div className="text-sm text-slate-600 mt-1">
            {job.property_address || "—"}
            {job.unit && <span className="ml-1">· Unit {job.unit}</span>}
          </div>
          <div className="mt-2 flex flex-wrap gap-2 print:hidden">
            <button onClick={() => window.print()}
              data-testid="legal-protocol-print-btn"
              className="h-8 px-3 border-2 border-slate-300 hover:bg-slate-100 text-slate-800 font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center gap-1">
              <Printer size={12}/> Print / Save PDF
            </button>
            <Link to="/admin/legal-radar"
              className="h-8 px-3 border-2 border-slate-300 hover:bg-slate-100 text-slate-800 font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center gap-1">
              <FilePdf size={12}/> Open Legal Radar
            </Link>
          </div>
        </header>

        {/* Reuse the same panel that renders on the WO detail — it already
            handles empty-state, tag-as-legal, checklist, moisture readings. */}
        <LegalProtocolPanel jobId={id}/>

        {/* Chain-of-custody summary — appended for the compliance packet. */}
        {lp.enabled && (
          <section className="mt-6 text-xs text-slate-600 leading-relaxed">
            <h2 className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
              Timeline
            </h2>
            <ul className="space-y-1 list-none">
              {lp.auto_attached_at && (
                <li>
                  <span className="tabular-nums text-slate-500">{new Date(lp.auto_attached_at).toLocaleString()}</span>
                  <span className="ml-2">
                    Protocol {lp.auto_attached ? "auto-attached" : "manually enabled"}
                    {lp.manually_enabled_by ? ` by ${lp.manually_enabled_by}` : ""}
                    {" "}({lp.category_label})
                  </span>
                </li>
              )}
              {lp.acknowledged_at && (
                <li>
                  <span className="tabular-nums text-slate-500">{new Date(lp.acknowledged_at).toLocaleString()}</span>
                  <span className="ml-2">Acknowledged by <b>{lp.acknowledged_by_name || "—"}</b></span>
                </li>
              )}
              {(lp.moisture_readings || []).map(r => (
                <li key={r.id}>
                  <span className="tabular-nums text-slate-500">{new Date(r.logged_at).toLocaleString()}</span>
                  <span className="ml-2">
                    Moisture reading @ <b>{r.location}</b> — {r.dry ? "DRY" : (r.reading_value != null ? `${r.reading_value}${r.unit || "%"}` : "wet")}
                    {r.logged_by_name && <span className="text-slate-500 ml-1">by {r.logged_by_name}</span>}
                  </span>
                </li>
              ))}
              {(lp.checklist || []).filter(c => c.done && c.done_at).map(c => (
                <li key={`ck-${c.id}`}>
                  <span className="tabular-nums text-slate-500">{new Date(c.done_at).toLocaleString()}</span>
                  <span className="ml-2">
                    ✓ <span className="text-slate-800">{c.text}</span>
                    {c.done_by_name && <span className="text-slate-500 ml-1">by {c.done_by_name}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!lp.enabled && (
          <div className="mt-4 text-sm text-slate-500 italic">
            This WO has no active Legal Protocol. Use the "Tag as Legal · retroactive" button above to attach one.
          </div>
        )}
      </div>
    </AdminShell>
  );
}
